import React, { useState } from 'react';

// cw: Reusable Thank You (acknowledgements) request popup. Opened from the Members page for a
// future/deferred TY. The recipient is pre-filled (chosen from the row), so there's no
// "select member" dropdown here. onSubmit receives the chosen type + note; the parent decides
// where it goes (e.g. the deferred-TY queue) and closes the modal.
type Props = {
    open: boolean;
    recipientName: string;
    receivedById?: string;      // kept for caller compatibility; not used here
    testimonialLabel: string;
    submitting?: boolean;
    onClose: () => void;
    onSubmit: (thankYouType: string, note: string) => void;
};

const ThankYouRequestModal: React.FC<Props> = ({
    open,
    recipientName,
    testimonialLabel,
    submitting = false,
    onClose,
    onSubmit,
}) => {
    const [type, setType] = useState('');
    const [note, setNote] = useState('');

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4" onMouseDown={onClose}>
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-1">
                    <h3 className="text-lg font-semibold text-gray-900">Request a {testimonialLabel}</h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                    Submit a {testimonialLabel} request. Your group facilitator will review it during the meeting.
                </p>

                <div className="mb-3">
                    <label className="block text-sm font-medium mb-1">Who are you thanking?</label>
                    <input
                        type="text"
                        value={recipientName}
                        readOnly
                        className="w-full border rounded-lg p-2 text-sm bg-gray-50 text-gray-700"
                    />
                </div>

                <div className="mb-3">
                    <label className="block text-sm font-medium mb-1">Type (optional)</label>
                    <select className="w-full border rounded-lg p-2 text-sm" value={type} onChange={(e) => setType(e.target.value)}>
                        <option value="">Select type...</option>
                        <option value="referral">Referral</option>
                        <option value="introduction">Introduction</option>
                        <option value="business_outcome">Business Outcome</option>
                        <option value="other">Other</option>
                    </select>
                </div>

                <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium">Note (optional)</label>
                        {note && (
                            <button type="button" onClick={() => setNote('')} className="text-xs text-red-500 hover:text-red-700">Clear</button>
                        )}
                    </div>
                    <p className="text-xs text-gray-500 mb-1">A quick note about why you're thanking this person.</p>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={note.length > 120 ? 8 : 3}
                        placeholder="e.g. introduced me to a client who hired me"
                        className="w-full border rounded-lg p-2 text-sm"
                    />
                </div>

                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-100" disabled={submitting}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => onSubmit(type, note)}
                        disabled={submitting}
                        className="px-4 py-2 bg-[#EF7A2B] text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                    >
                        {submitting ? 'Submitting…' : 'Submit Request'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ThankYouRequestModal;
